import type { PaletteColor } from '@/components/shared/color/types'
import type { TmCountryCode, PhoneType, UrlConstraint, DeviceType } from '@/services/types'
import type { CustomFieldType } from '@/services/domain/cusomFields/types'

export type WebWidgetPaletteColor = Extract<
  PaletteColor,
  'blue' | 'orange' | 'green' | 'red' | 'cyan' | 'violet' | 'purpur' | 'brown'
>

export enum WebWidgetThemeColorTypes {
  GRADIENT = 'gradient',
  SOLID = 'solid',
  CUSTOM = 'custom',
}

export enum WebWidgetDetailsMinimizedStateAlignTo {
  RIGHT = 'right',
  LEFT = 'left',
}

export enum WebWidgetDetailsMinimizedStateIcon {
  TEXTMAGIC = 'textmagic',
  CHAT = 'chat',
  MESSAGE = 'message',
  PHONE = 'phone',
  EMAIL = 'email',
}

// Prefix used to encode a contact custom field inside the frontend-owned fieldSorting array (e.g. "customField:123").
export const WEB_WIDGET_CUSTOM_FIELD_SORTING_PREFIX = 'customField:'
export const WEB_WIDGET_CUSTOM_FIELD_LABEL_MAX_LENGTH = 50
export type WebWidgetCustomFieldName = `${typeof WEB_WIDGET_CUSTOM_FIELD_SORTING_PREFIX}${string}`

export type WebWidgetDetailsEmailFieldName = 'name' | 'email' | 'message' | 'phone' | WebWidgetCustomFieldName
export type WebWidgetDetailsSmsFieldName = 'name' | 'phone' | 'message'
export type WebWidgetDetailsPreChatFieldName = 'name' | 'email' | 'message' | 'phone' | WebWidgetCustomFieldName

export type WebWidgetChannelCustomFieldSetting = {
  fieldId: number
  required: boolean
  // Runtime widget falls back to title
  label?: string | null
}

export type WebWidgetChannelFieldSettings = {
  name: boolean
  phone: boolean
  phoneRequired: boolean
  customFields: WebWidgetChannelCustomFieldSetting[]
}

export type WebWidgetRuntimeCustomFieldOption = {
  id: number
  value: string
}

// Inline metadata - the runtime widget bundle has no access to the schema service
export type WebWidgetRuntimeCustomField = {
  id: number
  title: string
  type: CustomFieldType
  required: boolean
  options: WebWidgetRuntimeCustomFieldOption[]
}

export type WebWidgetCustomColor = {
  primaryColor: {
    background: string
    font: string
  }
  buttonColor: {
    background: string
    font: string
  }
}

export type WebWidgetDetailsColorTheme = {
  type: WebWidgetThemeColorTypes
  color: WebWidgetPaletteColor
  custom: WebWidgetCustomColor
}

export type WebWidgetDetailsMinimizedState = {
  buttonTextEnabled: boolean
  buttonText: string
  icon: WebWidgetDetailsMinimizedStateIcon
  borderRadius: number
  alignTo: WebWidgetDetailsMinimizedStateAlignTo
  sideSpacing: number
  bottomSpacing: number
  eyeCatcherEnabled: boolean
  eyeCatcherText: string
  eyeCatcherDelay: number
}

export type WebWidgetDetailsWelcomeScreen = {
  greeting: string
  intro: string
}

export type WebWidgetDetailsAppearance = {
  colorTheme: WebWidgetDetailsColorTheme
  minimizedState: WebWidgetDetailsMinimizedState
  welcomeScreen: WebWidgetDetailsWelcomeScreen
}

export type WebWidgetInbox = {
  id: string
  email: string
}

export type WebWidgetEmailChannel<UI_SETTINGS = string /* JSON */> = {
  isActive: boolean
  email: string
  inbox: WebWidgetInbox
  uiSettings: UI_SETTINGS
  listId?: string
  fieldSettings: WebWidgetChannelFieldSettings
  customFields?: WebWidgetRuntimeCustomField[]
}

export type WebWidgetEmailChannelRequest = {
  isActive: boolean
  uiSettings: string
  email: string | null
  inboxId: string | null
  listId: string | null
  fieldSettings: WebWidgetChannelFieldSettings
}

export type WebWidgetParsedEmailChannel = WebWidgetEmailChannel<WebWidgetDetailsEmailChannelSettingsUi>

export type WebWidgetUserPhone = {
  id: string
  type: PhoneType
}

export type WebWidgetUserPhoneResponse = {
  id: string
  countryId: TmCountryCode
  phone: string
  phoneType: PhoneType
}

export type WebWidgetDetailsCustomNumber = {
  countryId: TmCountryCode
  phone: string
}

export type WebWidgetDetailsGlobalNumberRequest = {
  globalNumber?: WebWidgetUserPhone
  customGlobalNumber?: WebWidgetDetailsCustomNumber
}

export type WebWidgetChannelNumberRequest = {
  countryId: TmCountryCode
  number?: WebWidgetUserPhone
  customNumber?: WebWidgetDetailsCustomNumber
}

export type WebWidgetFacebookPage = {
  id: string
  name: string
}

export type WebWidgetInstagramAccount = {
  id: string
  name: string
}

export type WebWidgetSmsChannelRequest = {
  isActive: boolean
  uiSettings: string
  globalNumber?: WebWidgetUserPhone
  customGlobalNumber?: WebWidgetDetailsCustomNumber
  numbers: WebWidgetChannelNumberRequest[]
}

export type WebWidgetSmsChannel<UI_SETTINGS = string /* JSON */> = {
  isActive: boolean
  uiSettings: UI_SETTINGS
  globalNumber?: WebWidgetUserPhoneResponse
  customGlobalNumber?: WebWidgetDetailsCustomNumber
  numbers: Array<{
    countryId: TmCountryCode
    number?: WebWidgetUserPhoneResponse
    customNumber?: WebWidgetDetailsCustomNumber
  }>
}

export type WebWidgetParsedSmsChannel = WebWidgetSmsChannel<WebWidgetDetailsSmsChannelSettingsUi>

export type WebWidgetCallChannel<UI_SETTINGS = string /* JSON */> = {
  isActive: boolean
  uiSettings: UI_SETTINGS
  globalNumber?: WebWidgetUserPhoneResponse
  customGlobalNumber?: WebWidgetDetailsCustomNumber
  numbers: Array<{
    countryId: TmCountryCode
    number?: WebWidgetUserPhoneResponse
    customNumber?: WebWidgetDetailsCustomNumber
  }>
}

export type WebWidgetCallChannelRequest = {
  isActive: boolean
  uiSettings: string
  globalNumber?: WebWidgetUserPhone
  customGlobalNumber?: WebWidgetDetailsCustomNumber
  numbers: WebWidgetChannelNumberRequest[]
}

export type WebWidgetParsedCallChannel = WebWidgetCallChannel<WebWidgetDetailsCallChannelSettingsUi>

export type WebWidgetWhatsappChannel<UI_SETTINGS = string /* JSON */> = {
  isActive: boolean
  uiSettings: UI_SETTINGS
  globalNumber?: WebWidgetUserPhoneResponse
  customGlobalNumber?: WebWidgetDetailsCustomNumber
}

export type WebWidgetWhatsappChannelRequest = {
  isActive: boolean
  uiSettings: string
  globalNumber: WebWidgetUserPhone
  customGlobalNumber?: WebWidgetDetailsCustomNumber
}

export type WebWidgetParsedWhatsappChannel = WebWidgetWhatsappChannel<WebWidgetDetailsWhatsappChannelSettingsUi>

export type WebWidgetFacebookChannel<UI_SETTINGS = string /* JSON */> = {
  isActive: boolean
  uiSettings: UI_SETTINGS
  facebookPage?: WebWidgetFacebookPage
  facebookPageCustomLink?: string
}

export type WebWidgetFacebookChannelRequest = {
  isActive: boolean
  uiSettings: string
  facebookPageId?: string
  facebookPageCustomLink?: string
}

export type WebWidgetParsedFacebookChannel = WebWidgetFacebookChannel<WebWidgetDetailsFacebookChannelSettingsUi>

export type WebWidgetInstagramChannel<UI_SETTINGS = string /* JSON */> = {
  isActive: boolean
  uiSettings: UI_SETTINGS
  instagramAccount?: WebWidgetInstagramAccount
  instagramAccountCustomLink?: string
}

export type WebWidgetInstagramChannelRequest = {
  isActive: boolean
  uiSettings: string
  instagramAccountId?: string
  instagramAccountCustomLink?: string
}

export type WebWidgetParsedInstagramChannel = WebWidgetInstagramChannel<WebWidgetDetailsInstagramChannelSettingsUi>

export type WebWidgetChatChannel<UI_SETTINGS = string /* JSON */> = {
  isActive: boolean
  uiSettings: UI_SETTINGS
  fieldSettings: WebWidgetChannelFieldSettings
  listId?: string
  autoReplyMessage: string
  rejectTimeout: number
  rejectMessage: string
  isSatisfactionSurveyShown: boolean
  isHidden: boolean
  customFields?: WebWidgetRuntimeCustomField[]
}

export type WebWidgetChatChannelRequest = {
  isActive: boolean
  uiSettings: string
  listId: string | null
  fieldSettings: WebWidgetChannelFieldSettings
  autoReplyMessage: string
  rejectTimeout: number
  rejectMessage: string
  isSatisfactionSurveyShown: boolean
  isHidden: boolean
}

export type WebWidgetParsedChatChannel = WebWidgetChatChannel<WebWidgetDetailsChatChannelSettingsUi>

export type WebWidgetDisabledChannel<T> = Partial<T> & {
  isActive: false
}

export enum WebWidgetChannelType {
  SMS = 'sms',
  CALL = 'call',
  EMAIL = 'email',
  CHAT = 'chat',
  WHATSAPP = 'whatsapp',
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
}

export type WebWidgetDetailsChannelsResponse = {
  [WebWidgetChannelType.SMS]: WebWidgetSmsChannel | null
  [WebWidgetChannelType.EMAIL]: WebWidgetEmailChannel | null
  [WebWidgetChannelType.CALL]: WebWidgetCallChannel | null
  [WebWidgetChannelType.CHAT]: WebWidgetChatChannel | null
  [WebWidgetChannelType.WHATSAPP]: WebWidgetWhatsappChannel | null
  [WebWidgetChannelType.FACEBOOK]: WebWidgetFacebookChannel | null
  [WebWidgetChannelType.INSTAGRAM]: WebWidgetInstagramChannel | null
}

export type WebWidgetDetailsChannelsRequest = {
  [WebWidgetChannelType.SMS]: WebWidgetSmsChannelRequest | WebWidgetDisabledChannel<WebWidgetSmsChannelRequest> | null
  [WebWidgetChannelType.EMAIL]:
    | WebWidgetEmailChannelRequest
    | WebWidgetDisabledChannel<WebWidgetEmailChannelRequest>
    | null
  [WebWidgetChannelType.CALL]:
    | WebWidgetCallChannelRequest
    | WebWidgetDisabledChannel<WebWidgetCallChannelRequest>
    | null
  [WebWidgetChannelType.CHAT]?:
    | WebWidgetChatChannelRequest
    | WebWidgetDisabledChannel<WebWidgetChatChannelRequest>
    | null
  [WebWidgetChannelType.WHATSAPP]:
    | WebWidgetWhatsappChannelRequest
    | WebWidgetDisabledChannel<WebWidgetWhatsappChannelRequest>
    | null
  [WebWidgetChannelType.FACEBOOK]:
    | WebWidgetFacebookChannelRequest
    | WebWidgetDisabledChannel<WebWidgetFacebookChannelRequest>
    | null
  [WebWidgetChannelType.INSTAGRAM]:
    | WebWidgetInstagramChannelRequest
    | WebWidgetDisabledChannel<WebWidgetInstagramChannelRequest>
    | null
}

export type WebWidgetDetailsChannelSorting = WebWidgetChannelType[]

export enum WebWidgetDetailsTargetType {
  all = 'all',
  allow = 'allow',
  disallow = 'disallow',
}

export enum WebWidgetDetailsScheduleTimeType {
  ALWAYS = 'always',
  CUSTOM = 'custom',
}

export type WebWidgetDetailsTargetCountries = {
  type: WebWidgetDetailsTargetType
  allowCountries: TmCountryCode[]
  disallowCountries: TmCountryCode[]
}

export type WebWidgetDetailsScheduleTimeItem = {
  day: string
  enabled: boolean
  schedule: {
    from: string
    to: string
  }[]
}

export type WebWidgetDetailsSchedule = {
  timeType: WebWidgetDetailsScheduleTimeType
  timezoneId: string
  timeParams: WebWidgetDetailsScheduleTimeItem[]
}

export type WebWidgetDetailsUrlRule = {
  constraint: UrlConstraint
  value: string
}

export type WebWidgetDetailsPageUrls = {
  urlDomain: string
  type: WebWidgetDetailsTargetType
  allowPages: WebWidgetDetailsUrlRule[]
  disallowPages: WebWidgetDetailsUrlRule[]
}

export type WebWidgetDetailsTarget = {
  countries: WebWidgetDetailsTargetCountries
  schedule: WebWidgetDetailsSchedule
  pageUrls: WebWidgetDetailsPageUrls
  deviceType: DeviceType
}

export type WebWidgetDetailsEmailChannelSettingsUi = {
  name: string
  mainScreen: {
    title: string
    description: string
    buttonText: string
    fieldSorting: WebWidgetDetailsEmailFieldName[]
  }
  successScreen: {
    title: string
    description: string
  }
}

export type WebWidgetDetailsSmsChannelSettingsUi = {
  name: string
  showQrCode: boolean
  mainScreen: {
    title: string
    description: string
    buttonText: string
  }
  successScreen: {
    title: string
    description: string
  }
}

export type WebWidgetDetailsCallChannelSettingsUi = {
  name: string
  showQrCode: boolean
  mainScreen: {
    title: string
    description: string
    buttonText: string
  }
}

export type WebWidgetDetailsWhatsappChannelSettingsUi = {
  name: string
  showQrCode: boolean
  mainScreen: {
    title: string
    description: string
    buttonText: string
  }
}

export type WebWidgetDetailsFacebookChannelSettingsUi = {
  name: string
  showQrCode: boolean
  mainScreen: {
    title: string
    description: string
    buttonText: string
  }
}

export type WebWidgetDetailsInstagramChannelSettingsUi = {
  name: string
  showQrCode: boolean
  mainScreen: {
    title: string
    description: string
    buttonText: string
  }
}

export type WebWidgetDetailsChatChannelSettingsUi = {
  name: string
  fieldLabel: string
  buttonLabel: string
  preChatScreen: {
    title: string
    buttonText: string
    fieldSorting: WebWidgetDetailsPreChatFieldName[]
  }
  offlineMessage: string
}
